import * as Crypto from "expo-crypto";

import { conversationDeckApi } from "@/api";
import { storage } from "@/lib/storage";
import { isPlusUser } from "@/lib/plan";
import { useAuthStore } from "@/state/auth-store";
import { registerSessionReset } from "@/state/session-reset";
import {
  CONVERSATION_DECK_SERVER_CATEGORIES,
  type ConversationDeckCategory,
  type ConversationDeckServerCategory,
} from "@/types";
import {
  applyConversationDeckRefill,
  createConversationDeckCache,
  isConversationDeckCacheValid,
  takeConversationDeckHelper,
  validateBootstrapResponse,
  validateRefillResponse,
} from "./conversation-deck-cache-core";
import { conversationDeckPersistence } from "./conversation-deck-persistence";

export const CONVERSATION_PROMPT_CATEGORIES: readonly ConversationDeckCategory[] =
  [
    "start",
    "keep_going",
    "fun",
    "get_to_know",
    "go_deeper",
    "make_a_plan",
    "wrap_up",
  ];

export type ConversationPromptCategory = ConversationDeckCategory;

export type ConversationPromptSuggestion = {
  id: string;
  category: ConversationDeckServerCategory;
  text: string;
  followUpText: string;
};

const INSTALLATION_ID_KEY = "opncue.conversation-deck.installation";
const BOOTSTRAP_OPERATION_PREFIX = "opncue.conversation-deck.bootstrap";
const REFILL_OPERATION_PREFIX = "opncue.conversation-deck.refill";

type PendingOperation = {
  idempotencyKey: string;
  unusedCount?: number;
};

const bootstrapInFlight = new Map<string, Promise<void>>();
const refillInFlight = new Map<string, Promise<void>>();
let cacheEpoch = 0;

/**
 * The account's plan right now, read at the moment of use.
 *
 * Read from the store rather than passed in because this client is a module,
 * not a component, and the plan can change while the app is open (a purchase,
 * a lapse, a referral grant). The cache records the plan it was built under,
 * so a change makes the cache invalid and the next read re-bootstraps —
 * which is what makes an upgrade fill the three paid decks and a downgrade
 * drop them from the device.
 */
function hasPlusNow(): boolean {
  return isPlusUser(useAuthStore.getState().user);
}

type CacheTakeResult = {
  suggestion: ConversationPromptSuggestion | null;
  refillCategory: ConversationDeckServerCategory | null;
  unusedCount: number;
};

async function installationId(): Promise<string> {
  const existing = await storage.get(INSTALLATION_ID_KEY);
  if (existing) return existing;
  const created = Crypto.randomUUID();
  await storage.set(INSTALLATION_ID_KEY, created);
  return created;
}

async function pendingOperation(
  key: string,
  unusedCount?: number,
): Promise<PendingOperation> {
  const raw = await storage.get(key);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as PendingOperation;
      if (parsed.idempotencyKey) return parsed;
    } catch {
      await storage.remove(key);
    }
  }
  const operation = {
    idempotencyKey: Crypto.randomUUID(),
    ...(unusedCount === undefined ? {} : { unusedCount }),
  };
  await storage.set(key, JSON.stringify(operation));
  return operation;
}

async function ensureReady(ownerUserId: string): Promise<void> {
  const current = await conversationDeckPersistence.read();
  if (isConversationDeckCacheValid(current, ownerUserId, hasPlusNow())) return;
  if (current) await conversationDeckPersistence.clear();

  let bootstrap = bootstrapInFlight.get(ownerUserId);
  if (!bootstrap) {
    const startedAtEpoch = cacheEpoch;
    bootstrap = (async () => {
      const operationKey = `${BOOTSTRAP_OPERATION_PREFIX}.${ownerUserId}`;
      const operation = await pendingOperation(operationKey);
      const response = await conversationDeckApi.bootstrap(
        await installationId(),
        operation.idempotencyKey,
      );
      // Read once, and used for BOTH the check and the cache it stamps. Read
      // twice, a plan that changed mid-bootstrap would validate the response
      // against one expectation and label the cache with the other — a cache
      // that then looks valid forever while holding the wrong decks.
      const plus = hasPlusNow();
      let valid;
      try {
        valid = validateBootstrapResponse(response, plus);
      } catch (error) {
        // Drop the idempotency key before giving up. The server REPLAYS a
        // reservation for 24 hours, so retrying with the same key returns the
        // same response — if that response is one this plan cannot accept
        // (a reservation made before an upgrade, a lapse, or a change to which
        // decks are paid), the device would re-fetch the identical rejected
        // deck on every open for a day and show an empty sheet the whole time.
        // Clearing it means the next attempt reserves afresh.
        //
        // Only on a REJECTED response. A network failure keeps its key, because
        // there the reservation may well be fine and re-reserving would burn a
        // fresh one for nothing.
        await storage.remove(operationKey);
        throw error;
      }
      if (startedAtEpoch !== cacheEpoch) {
        throw new Error("Conversation Deck session changed during bootstrap.");
      }
      await conversationDeckPersistence.mutate(() =>
        createConversationDeckCache(ownerUserId, valid, plus),
      );
      await storage.remove(operationKey);
    })().finally(() => {
      bootstrapInFlight.delete(ownerUserId);
    });
    bootstrapInFlight.set(ownerUserId, bootstrap);
  }
  await bootstrap;
}

async function refill(
  ownerUserId: string,
  category: ConversationDeckServerCategory,
  unusedCount: number,
): Promise<void> {
  const flightKey = `${ownerUserId}:${category}`;
  const existingFlight = refillInFlight.get(flightKey);
  if (existingFlight) return existingFlight;

  const request = (async () => {
    const operationKey = `${REFILL_OPERATION_PREFIX}.${ownerUserId}.${category}`;
    const operation = await pendingOperation(operationKey, unusedCount);
    let response;
    try {
      response = await conversationDeckApi.refill(
        await installationId(),
        operation.idempotencyKey,
        category,
        operation.unusedCount ?? unusedCount,
      );
    } catch {
      // Keep the idempotency key so the next attempt retries the same server
      // reservation instead of advancing the account sequence again.
      return;
    }

    try {
      const valid = validateRefillResponse(response);
      await conversationDeckPersistence.mutate((current) => {
        if (!isConversationDeckCacheValid(current, ownerUserId, hasPlusNow())) {
          throw new Error("Conversation Deck cache owner changed.");
        }
        return applyConversationDeckRefill(current, valid);
      });
      await storage.remove(operationKey);
    } catch {
      await conversationDeckPersistence.clear();
      await storage.remove(operationKey);
    }
  })().finally(() => {
    refillInFlight.delete(flightKey);
  });
  refillInFlight.set(flightKey, request);
  return request;
}

async function takeFromCache(
  ownerUserId: string,
  category: ConversationPromptCategory,
): Promise<CacheTakeResult> {
  let result: CacheTakeResult = {
    suggestion: null,
    refillCategory: null,
    unusedCount: 0,
  };
  await conversationDeckPersistence.mutate((current) => {
    if (!isConversationDeckCacheValid(current, ownerUserId, hasPlusNow())) {
      throw new Error("Conversation Deck cache is unavailable.");
    }
    const next = takeConversationDeckHelper(current, category);
    if (!next.selection) return current;
    result = {
      refillCategory: next.selection.refillCategory,
      unusedCount: next.selection.unusedCount,
      suggestion: {
        id: next.selection.helper.id,
        category: next.selection.helper.category,
        text: next.selection.helper.main,
        followUpText: next.selection.helper.possibleFollowUp,
      },
    };
    return next.state;
  });
  return result;
}

export const conversationDeckClient = {
  async next(
    ownerUserId: string,
    category: ConversationPromptCategory,
  ): Promise<ConversationPromptSuggestion | null> {
    await ensureReady(ownerUserId);
    let result = await takeFromCache(ownerUserId, category);

    if (!result.suggestion) {
      await refill(ownerUserId, category, 0);
      result = await takeFromCache(ownerUserId, category);
    }
    if (result.refillCategory) {
      void refill(ownerUserId, result.refillCategory, result.unusedCount);
    }
    return result.suggestion;
  },

  async clear(): Promise<void> {
    cacheEpoch += 1;
    bootstrapInFlight.clear();
    refillInFlight.clear();
    await conversationDeckPersistence.clear();
  },
};

registerSessionReset(() => conversationDeckClient.clear());

export { CONVERSATION_DECK_SERVER_CATEGORIES };
